import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  XMarkIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { FormSubmission } from '../../types/Form';

interface OperationResult {
  submissionId: number;
  submissionName: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  error?: string;
  leadId?: number;
}

interface BulkCreateLeadsModalProps {
  isOpen: boolean;
  selectedSubmissions: FormSubmission[];
  onConfirm: (
    onProgress: (current: number, total: number, result: OperationResult) => void
  ) => Promise<OperationResult[]>;
  onClose: () => void;
}

const BulkCreateLeadsModal: React.FC<BulkCreateLeadsModalProps> = ({
  isOpen,
  selectedSubmissions,
  onConfirm,
  onClose
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<OperationResult[]>([]);
  const [currentProgress, setCurrentProgress] = useState(0);
  const [showDetails, setShowDetails] = useState(true);

  const total = selectedSubmissions.length;
  const successful = results.filter(r => r.status === 'success').length;
  const failed = results.filter(r => r.status === 'error').length;
  const pending = results.filter(r => r.status === 'pending').length;
  const progress = total > 0 ? Math.round((currentProgress / total) * 100) : 0;
  const isComplete = currentProgress === total && isProcessing;

  useEffect(() => {
    if (isOpen && !isProcessing) {
      // Initialize results
      const initialResults: OperationResult[] = selectedSubmissions.map(s => ({
        submissionId: s.id,
        submissionName: s.displayInfo?.name || s.submitterName || 'Anonymous',
        status: 'pending'
      }));
      setResults(initialResults);
      setCurrentProgress(0);
    }
  }, [isOpen, selectedSubmissions, isProcessing]);

  const handleStart = async () => {
    setIsProcessing(true);
    
    try {
      await onConfirm((current, _total, result) => {
        setCurrentProgress(current);
        setResults(prev => 
          prev.map(r => 
            r.submissionId === result.submissionId ? result : r
          )
        );
      });
    } catch (error) {
      console.error('Bulk create leads failed:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isProcessing && !isComplete) {
      if (!window.confirm('Operation is in progress. Are you sure you want to cancel?')) {
        return;
      }
    }
    setResults([]);
    setCurrentProgress(0);
    setIsProcessing(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            {isProcessing ? `Creating ${total} Leads` : `Create ${total} Leads from Submissions`}
          </h3>
          <button
            onClick={handleClose}
            disabled={isProcessing && !isComplete}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!isProcessing && currentProgress === 0 ? (
            // Pre-confirmation view
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  You are about to create <strong>{total}</strong> new {total === 1 ? 'lead' : 'leads'} from the selected form submissions.
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Selected Submissions:</h4>
                <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  {selectedSubmissions.map((submission, index) => (
                    <div key={submission.id} className="px-4 py-3 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {submission.displayInfo?.name || submission.submitterName || 'Anonymous'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {submission.displayInfo?.email || submission.submitterEmail}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500">#{index + 1}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-semibold mb-1">Important:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Duplicate emails will be detected and handled</li>
                      <li>Submissions will be marked as "attached" after lead creation</li>
                      <li>This process may take a few moments for large selections</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Progress view
            <div className="space-y-4">
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {isComplete ? 'Complete!' : `Processing... ${currentProgress} of ${total}`}
                  </span>
                  <span className="text-sm font-semibold text-blue-600">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <CheckCircleIcon className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-green-900">{successful}</p>
                  <p className="text-xs text-green-700">Successful</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <XCircleIcon className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-red-900">{failed}</p>
                  <p className="text-xs text-red-700">Failed</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <ClockIcon className="w-6 h-6 text-gray-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-gray-900">{pending}</p>
                  <p className="text-xs text-gray-700">Pending</p>
                </div>
              </div>

              {/* Toggle Details */}
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {showDetails ? 'Hide' : 'Show'} Details
              </button>

              {/* Results List */}
              {showDetails && (
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {results.map((result, index) => (
                    <div
                      key={result.submissionId}
                      className={`px-4 py-3 border-b border-gray-100 last:border-b-0 flex items-center justify-between ${
                        result.status === 'success' ? 'bg-green-50' :
                        result.status === 'error' ? 'bg-red-50' :
                        result.status === 'processing' ? 'bg-blue-50' :
                        'bg-white'
                      }`}
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="flex items-center justify-center w-6">
                          {result.status === 'success' && (
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          )}
                          {result.status === 'error' && (
                            <XCircleIcon className="w-5 h-5 text-red-600" />
                          )}
                          {result.status === 'processing' && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          )}
                          {result.status === 'pending' && (
                            <ClockIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {result.submissionName}
                          </p>
                          {result.error && (
                            <p className="text-xs text-red-600 truncate">{result.error}</p>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">#{index + 1}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Error Summary */}
              {failed > 0 && isComplete && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-800 font-semibold mb-2">
                    {failed} {failed === 1 ? 'submission' : 'submissions'} failed to convert:
                  </p>
                  <ul className="text-xs text-red-700 space-y-1">
                    {results
                      .filter(r => r.status === 'error')
                      .map(r => (
                        <li key={r.submissionId}>• {r.submissionName}: {r.error}</li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex justify-end space-x-3 flex-shrink-0">
          {!isProcessing && currentProgress === 0 ? (
            // Pre-start buttons
            <>
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStart}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <CheckCircleIcon className="w-4 h-4" />
                <span>Start Creating Leads</span>
              </button>
            </>
          ) : (
            // Post-processing buttons
            <>
              {isComplete ? (
                <>
                  {successful > 0 && (
                    <button
                      onClick={() => {
                        // Navigate to leads page or show created leads
                        handleClose();
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      View Created Leads
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Close
                  </button>
                </>
              ) : (
                <button
                  onClick={handleClose}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel Operation
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkCreateLeadsModal;
